import React, { useState, useEffect, useRef } from 'react';
import { ScrollText, Download, RefreshCw, Search, AlertCircle } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';
import api from '../lib/api';
import OpenFecProgressSummary from './data-import/OpenFecProgressSummary';
import { normalizeOpenFecProgress, OpenFecProgress } from '../utils/openFecProgress';

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  jobId: string;
  /** Parsed `context` / legacy `meta` JSON object for structured log lines. */
  meta?: Record<string, unknown>;
}

interface JobLogViewerProps {
  jobId: string | number;
  isOpen: boolean;
  onClose: () => void;
}

function coerceLogMeta(value: unknown): Record<string, unknown> | undefined {
  if (value == null) return undefined;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

export function openFecEventContext(meta: Record<string, unknown>): string {
  const p = normalizeOpenFecProgress({ ...meta, kind: 'openfec' });
  if (!p) return '';
  const code = typeof meta.error_code === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(meta.error_code) ? ` · Code: ${meta.error_code}` : '';
  return `Source ${p.sourceId ?? 'unknown'} · Job ${p.jobId ?? 'unknown'} · Run ${p.runId ?? 'unknown'} · ${p.pagesSaved ?? 'unknown'} pages saved · ${p.rowsReceived ?? 'unknown'} rows received · ${p.requestsThisAttempt ?? 'unknown'} requests this attempt · ${p.retriesThisAttempt ?? 'unknown'} retries${code}`;
}

/** Metadata and event writes are independent: select the freshest valid snapshot. */
export function newestOpenFecSnapshot(metadata: unknown, events: { timestamp: string; context?: unknown }[]): OpenFecProgress | null {
  const candidates: { progress: OpenFecProgress; timestamp: number }[] = [];
  const add = (progress: OpenFecProgress | null, fallback?: string) => {
    if (!progress) return;
    const heartbeat = progress.heartbeatAt ? Date.parse(progress.heartbeatAt) : NaN;
    const eventTime = fallback ? Date.parse(fallback) : NaN;
    candidates.push({ progress, timestamp: Number.isFinite(heartbeat) ? heartbeat : Number.isFinite(eventTime) ? eventTime : -Infinity });
  };
  add(normalizeOpenFecProgress(metadata));
  for (const event of events) {
    const meta = coerceLogMeta(event.context);
    if (meta?.kind === 'openfec_event') add(normalizeOpenFecProgress({ ...meta, kind: 'openfec' }), event.timestamp);
  }
  return candidates.reduce<{ progress: OpenFecProgress; timestamp: number } | null>((latest, candidate) => {
    if (!latest || candidate.timestamp > latest.timestamp) return candidate;
    if (candidate.timestamp === latest.timestamp && candidate.progress.outcome && !latest.progress.outcome) return candidate;
    return latest;
  }, null)?.progress ?? null;
}

const JobLogViewer: React.FC<JobLogViewerProps> = ({ jobId, isOpen, onClose }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [openFecProgress, setOpenFecProgress] = useState<OpenFecProgress | null>(null);
  const [jobActive, setJobActive] = useState(false);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  interface DbLogRow {
    timestamp: string;
    level: string;
    message: string;
    context?: unknown;
  }

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (scrollRef.current && autoRefresh) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filteredLogs, autoRefresh]);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
      
      // Set up auto-refresh every 30 seconds
      const refreshInterval = setInterval(() => {
        if (autoRefresh && isOpen) {
          fetchLogs();
        }
      }, 30000); // 30 seconds

      return () => clearInterval(refreshInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoRefresh, jobId]);

  useEffect(() => {
    filterLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, searchQuery, levelFilter]);

  const fetchLogs = async () => {
    setIsLoading(true);
    setError(null);
    setOpenFecProgress(null);
    
    try {
      // Preferred path: DB-backed logs returned by /jobs/:id (works for all current importers)
      const detailsResponse = await api.get(`/data-management/jobs/${jobId}`);
      const details = detailsResponse.data?.data;
      const metadata = details?.job?.metadata;
      setJobActive(details?.job?.status === 'running');
      const dbLogs = (details?.logs || []) as DbLogRow[];
      setOpenFecProgress(newestOpenFecSnapshot(metadata?.progress, Array.isArray(dbLogs) ? dbLogs : []));

      if (Array.isArray(dbLogs) && dbLogs.length > 0) {
        const parsedDbLogs: LogEntry[] = dbLogs.map((row) => ({
          timestamp: row.timestamp || new Date().toISOString(),
          level: row.level || 'INFO',
          message: row.message || '',
          jobId: jobId.toString(),
          meta: coerceLogMeta(row.context),
        }));
        setLogs(parsedDbLogs);
        return;
      }

      // Fallback path: legacy text/JSONL endpoint
      const response = await api.get(`/data-management/jobs/${jobId}/log`);
      const raw = response.data;
      if (typeof raw === 'string') {
        const lines = raw.trim().split('\n').filter((line: string) => line.trim());
        const parsedLogs: LogEntry[] = lines.map((line: string) => {
          try {
            const parsed = JSON.parse(line) as Partial<LogEntry>;
            return {
              timestamp: parsed.timestamp || new Date().toISOString(),
              level: parsed.level || 'INFO',
              message: parsed.message || '',
              jobId: parsed.jobId || jobId.toString(),
              meta: coerceLogMeta(parsed.meta),
            };
          } catch {
            return {
              timestamp: new Date().toISOString(),
              level: 'INFO',
              message: line,
              jobId: jobId.toString(),
              meta: undefined,
            };
          }
        });
        setLogs(parsedLogs);
        return;
      }

      setLogs([]);
      setError('No logs available for this job yet.');
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setError(apiError.response?.data?.error || 'Failed to load logs');
      console.error('Failed to fetch logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(query) ||
        JSON.stringify(log.meta || {}).toLowerCase().includes(query)
      );
    }
    
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level.toLowerCase() === levelFilter.toLowerCase());
    }
    
    setFilteredLogs(filtered);
  };

  const handleDownload = async () => {
    try {
      const lines = logs.map((log) => JSON.stringify(log));
      const payload = `${lines.join('\n')}\n`;
      const url = window.URL.createObjectURL(new Blob([payload], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `job-${jobId}.log`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download log:', err);
    }
  };

  /** Coerce value to a string safe for React (never render an object). */
  const toDisplayString = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR': return 'bg-red-900 text-red-100 border-red-700';
      case 'WARN': return 'bg-yellow-900 text-yellow-100 border-yellow-700';
      case 'INFO': return 'bg-accent-900 text-accent-100 border-accent-700';
      case 'DEBUG': return 'bg-neutral-800 text-neutral-200 border-neutral-600';
      default: return 'bg-neutral-800 text-neutral-300 border-neutral-600';
    }
  };

  const latestProgress = (() => {
    for (let i = logs.length - 1; i >= 0; i -= 1) {
      const entry = logs[i];
      const staged = Number(entry.meta?.staged_rows);
      const source = Number(entry.meta?.source_rows);
      if (Number.isFinite(staged) && staged >= 0) {
        return {
          stagedRows: staged,
          sourceRows: Number.isFinite(source) && source > 0 ? source : null,
        };
      }
    }
    return null;
  })();

  const progressPercent = latestProgress?.sourceRows
    ? Math.max(0, Math.min(100, (latestProgress.stagedRows / latestProgress.sourceRows) * 100))
    : null;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={true}
      title="Job logs"
      description={`Job #${jobId}`}
      onClose={onClose}
      size="xl"
      className="sm:max-w-6xl"
      /* Search and level filter stay put while the log scrolls under them —
         the whole point of a log viewer is scrolling a long list. */
      subheader={
        <>
          {openFecProgress && <div className="mb-3"><OpenFecProgressSummary progress={openFecProgress} active={jobActive} /></div>}
          {!openFecProgress && latestProgress && (
            <div className="mb-3">
              <div className="flex justify-between items-center text-xs text-text-secondary dark:text-text-dark-secondary mb-1">
                <span>
                  Staged rows: {latestProgress.stagedRows.toLocaleString()}
                  {latestProgress.sourceRows ? ` / ${latestProgress.sourceRows.toLocaleString()}` : ''}
                </span>
                {progressPercent !== null && (
                  <span>{progressPercent.toFixed(1)}%</span>
                )}
              </div>
              <progress
                className="w-full h-2"
                value={progressPercent ?? 0}
                max={100}
              />
            </div>
          )}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border-light-secondary dark:border-border-dark-secondary rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-background-dark-secondary text-neutral-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-4 py-2 border border-border-light-secondary dark:border-border-dark-secondary rounded-lg bg-white dark:bg-background-dark-secondary text-neutral-900 dark:text-white"
            >
              <option value="all">All Levels</option>
              <option value="error">Error</option>
              <option value="warn">Warnings</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
            <button
              onClick={fetchLogs}
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </>
      }
      footer={
        <>
          <span className="mr-auto text-sm text-text-secondary dark:text-text-dark-secondary">
            Showing {filteredLogs.length} of {logs.length} log entries
          </span>
          {/*
            Auto-refresh is a toggle, not a confirm, so it lives in the footer
            beside the export rather than crowding the title row.
          */}
          <Button
            variant={autoRefresh ? 'primary' : 'secondary'}
            size="sm"
            icon={RefreshCw}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh on (30s)' : 'Auto-refresh off'}
          </Button>
          <Button variant="secondary" size="sm" icon={Download} onClick={handleDownload}>
            Download
          </Button>
        </>
      }
    >
        {/* Log Content */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 bg-neutral-900 font-mono text-sm"
        >
          {isLoading && logs.length === 0 && (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
              <p className="text-neutral-500">Loading logs...</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-300 mr-2" />
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          )}
          
          {!isLoading && logs.length === 0 && !error && (
            <div className="text-center py-8">
              <ScrollText className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
              <p className="text-neutral-500">No logs available</p>
            </div>
          )}
          
          {!isLoading && filteredLogs.length === 0 && logs.length > 0 && (
            <div className="text-center py-8">
              <p className="text-neutral-500">No logs match your filter criteria</p>
            </div>
          )}
          
          {!isLoading && filteredLogs.length > 0 && (
            <div className="space-y-0">
              {filteredLogs.map((log, index) => {
                // Format log message (coerce to string so we never render an object - React #31)
                const displayMessage = log.meta?.stage != null && log.meta?.step != null
                  ? `${toDisplayString(log.meta.stage)} - ${toDisplayString(log.meta.step)}`
                  : log.meta?.stage != null
                    ? toDisplayString(log.meta.stage)
                    : log.meta?.phase != null
                      ? `[${toDisplayString(log.meta.phase)}] ${toDisplayString(log.message)}`
                      : toDisplayString(log.message);
                const stagedRowsRaw = log.meta?.staged_rows;
                const sourceRowsRaw = log.meta?.source_rows;
                const stagedRows = Number(stagedRowsRaw);
                const sourceRows = Number(sourceRowsRaw);
                const hasStagedRows = Number.isFinite(stagedRows) && stagedRows >= 0;
                const hasSourceRows = Number.isFinite(sourceRows) && sourceRows > 0;
                const progressSuffix = hasStagedRows
                  ? hasSourceRows
                    ? ` (staged ${stagedRows.toLocaleString()} / ${sourceRows.toLocaleString()})`
                    : ` (staged ${stagedRows.toLocaleString()})`
                  : '';
                const displayMessageWithProgress = `${displayMessage}${progressSuffix}`;

                const timestamp = log.timestamp != null
                  ? new Date(log.timestamp).toLocaleString()
                  : '';

                return (
                  <div 
                    key={index}
                    className={`px-3 py-2 border-l-2 ${getLogLevelColor(toDisplayString(log.level))} hover:bg-opacity-80 transition-colors`}
                  >
                    <span className="text-text-tertiary dark:text-text-dark-tertiary">{timestamp}</span>
                    <span className="mx-2">[{toDisplayString(log.level)}]</span>
                    <span>{displayMessageWithProgress}</span>
                    {log.meta?.kind === 'openfec_event' && <p className="mt-1 break-all text-xs">{openFecEventContext(log.meta)}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

    </Modal>
  );
};

export default JobLogViewer;